import { useCallback, useEffect, useRef, useState } from "react";
import { acquireStreamClient, releaseStreamClient, streamTokenUserId } from "@/lib/streamClient";

// BLOQ Rooms audio on Stream Video (backend: POST /rooms/{id}/join → {stream:{api_key, token, call_type, call_id}, role}).
// Plays every remote participant, publishes the mic only while `canPublish` (host/speaker — enforced by role,
// the Stream token carries no publish grant). Remote audio is bound to hidden <audio> elements so the stage
// UI stays plain React (no SDK components needed).
export function useStreamAudio({ stream, canPublish }) {
  const callRef = useRef(null);
  const canPublishRef = useRef(canPublish);
  const [connected, setConnected] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [speakingIds, setSpeakingIds] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!stream?.token || !stream?.api_key || !stream?.call_id) return undefined;
    let cancelled = false;
    let client;
    let call;
    let sub;
    const bound = new Map(); // sessionId → { el, unbind }
    setError(null);

    (async () => {
      try {
        client = acquireStreamClient({
          apiKey: stream.api_key,
          user: { id: streamTokenUserId(stream.token) || "me" },
          token: stream.token,
        });
        call = client.call(stream.call_type || "default", stream.call_id);
        callRef.current = call;
        await call.camera.disable().catch(() => {});
        await call.microphone.disable().catch(() => {});
        if (cancelled) return;
        await call.join({ create: true });
        if (cancelled) { await call.leave().catch(() => {}); return; }
        setConnected(true);
        sub = call.state.participants$.subscribe((ps) => {
          const live = new Set();
          for (const p of ps) {
            if (p.isLocalParticipant) continue;
            live.add(p.sessionId);
            if (!bound.has(p.sessionId)) {
              const el = document.createElement("audio");
              el.autoplay = true;
              el.dataset.streamSession = p.sessionId;
              el.style.display = "none";
              document.body.appendChild(el);
              const unbind = call.bindAudioElement(el, p.sessionId, "audioTrack");
              bound.set(p.sessionId, { el, unbind });
            }
          }
          for (const [sid, b] of bound) {
            if (!live.has(sid)) { b.unbind?.(); b.el.remove(); bound.delete(sid); }
          }
          setSpeakingIds(ps.filter((p) => p.isSpeaking).map((p) => p.userId));
        });
        if (canPublishRef.current) {
          try {
            await call.microphone.enable();
            if (!cancelled) setMicOn(true);
          } catch {
            if (!cancelled) setError("mic");
          }
        }
      } catch {
        if (!cancelled) setError("connect");
      }
    })();

    return () => {
      cancelled = true;
      sub?.unsubscribe();
      for (const b of bound.values()) { b.unbind?.(); b.el.remove(); }
      bound.clear();
      const c = call;
      const cl = client;
      call = undefined;
      client = undefined;
      (async () => {
        await c?.leave().catch(() => {});
        await releaseStreamClient(cl);
      })();
      callRef.current = null;
      setConnected(false);
      setMicOn(false);
      setSpeakingIds([]);
    };
  }, [stream?.token, stream?.api_key, stream?.call_id, stream?.call_type]);

  // Stage role changed (promote/demote) → publish or stop publishing without re-joining.
  useEffect(() => {
    canPublishRef.current = canPublish;
    const call = callRef.current;
    if (!call || !connected) return;
    if (canPublish && !micOn) {
      call.microphone.enable().then(() => setMicOn(true)).catch(() => setError("mic"));
    } else if (!canPublish && micOn) {
      call.microphone.disable().then(() => setMicOn(false)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPublish, connected]);

  const toggleMic = useCallback(async () => {
    const call = callRef.current;
    if (!call || !canPublishRef.current) return;
    const next = !micOn;
    try {
      if (next) await call.microphone.enable();
      else await call.microphone.disable();
      setMicOn(next);
    } catch {
      setError("mic");
    }
  }, [micOn]);

  return { connected, micOn, toggleMic, speakingIds, error };
}
